import { useTranslations } from '@/i18n/use-translations.tsx';
import Widget from '@/components/common/widget.tsx';
import { Stack, Typography, Tooltip, IconButton } from '@mui/material';
import { Launch } from '@mui/icons-material';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import { SESSION_USAGE_ENTITIES } from '@/types/dtos/sessions/request/session-usage-entities.enum';
import { getFlowSlotTiming } from '@/components/events/helpers/get-flow-day-number.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import computeAllSlotTimings, { calculateFlowDurationFromSlots } from '@/components/flows/detail/slot-list/calculate';

export const SessionFlowsWidget = ({
  session,
  context = 'library',
  currentFlowId,
}: {
  session: SessionDto;
  context?: 'library' | 'flowStudio';
  currentFlowId?: string;
}) => {
  const t = useTranslations();
  const event = useEventStore((s) => s.event);
  const eventFlows = useEventStore((s) => s.eventFlows);

  const flows = useMemo(() => {
    let allFlows =
      session.usedInEntities
        ?.filter((entity) => entity.entityType === SESSION_USAGE_ENTITIES.FLOW && entity.entity)
        ?.map((entity) => entity.entity) || [];

    if (context === 'flowStudio' && currentFlowId && allFlows.length > 0) {
      allFlows = allFlows.filter((flow) => flow.id !== currentFlowId);
    }

    return allFlows;
  }, [session.usedInEntities, context, currentFlowId || null]);

  // Memoize computed slot timings per flow to avoid recomputing on every render
  // Pre-computes all slot timings once when eventFlows changes, then lookups are O(1)
  // Key: flow ID (or origin), Value: computed slots array
  const computedSlotsCache = useMemo(() => {
    const cache = new Map<string, FlowDto['slots']>();

    if (eventFlows) {
      eventFlows.forEach((flow) => {
        const flowId = flow.id || (typeof flow.origin === 'string' ? flow.origin : flow.origin?.id);
        if (!flowId || !flow.slots || flow.slots.length === 0) {
          return;
        }

        // Compute slot timings once per flow and cache by flow ID
        // This avoids recomputing inside the flowTimingMap loop
        const durationToUse = flow.slot?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(flow.slots || []);
        const computedSlots = computeAllSlotTimings(flow.slots || [], durationToUse);
        cache.set(flowId, computedSlots);
      });
    }

    return cache;
  }, [eventFlows]);

  const flowTimingMap = useMemo(() => {
    const flowTimingMap: Record<string, string> = {};

    if (event?.slots && event?.days && event?.startDate && flows && eventFlows) {
      flows.forEach((flow) => {
        // Find fresh flow data from eventFlows by matching ID or origin
        const freshFlow = eventFlows.find(
          (f) => f.id === flow.id || f.origin === flow.id || (typeof f.origin === 'object' && f.origin?.id === flow.id),
        );

        // Use fresh flow if available, otherwise fall back to stale data
        const flowToUse = freshFlow || flow;

        const flowSlotInEvent = event.slots.find(
          (slot) => slot.content === flowToUse.id || slot.content === flowToUse.origin,
        );

        // Find the slot containing this session
        let contentSlotInFlow = flowToUse.slots?.find((slot) => {
          if (slot.contentType !== 'Session') return false;
          const slotSessionId =
            typeof slot.content === 'string'
              ? slot.content
              : slot.content?.origin
                ? getObjectId(slot.content.origin)
                : slot.content?.id;
          const sessionId = session.origin ? getObjectId(session.origin) : session.id;
          return slotSessionId === sessionId || slotSessionId === session.id;
        });

        // Use cached computed slots if available, otherwise compute on-demand
        if (freshFlow && contentSlotInFlow && flowSlotInEvent) {
          const flowId =
            freshFlow.id || (typeof freshFlow.origin === 'string' ? freshFlow.origin : freshFlow.origin?.id);

          let computedSlot: typeof contentSlotInFlow | undefined;
          if (flowId && computedSlotsCache.has(flowId)) {
            // Use cached computed slots - O(1) lookup instead of O(n²) computation
            const cachedSlots = computedSlotsCache.get(flowId);
            computedSlot = cachedSlots?.find((slot) => slot.id === contentSlotInFlow?.id);
          } else if (freshFlow.slots) {
            // Fallback: compute on-demand if not in cache (shouldn't happen if eventFlows is up to date)
            const durationToUse =
              freshFlow.slot?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(freshFlow.slots || []);
            const computedSlots = computeAllSlotTimings(freshFlow.slots || [], durationToUse);
            computedSlot = computedSlots.find((slot) => slot.id === contentSlotInFlow?.id);
          }

          if (computedSlot) {
            contentSlotInFlow = computedSlot;
          }
        }

        if (flowSlotInEvent && contentSlotInFlow && freshFlow) {
          const dayTiming = getFlowSlotTiming(event, flowSlotInEvent, freshFlow, contentSlotInFlow);
          if (dayTiming) {
            flowTimingMap[flow.id] = dayTiming;
          }
        }
      });
    }

    return flowTimingMap;
  }, [event, flows, session, eventFlows, computedSlotsCache]);

  if (!flows || flows.length === 0) {
    return null;
  }

  const getWidgetTitle = () => {
    if (context === 'flowStudio') {
      return flows.length === 1
        ? t('session.alsoUsedInOtherFlows', { count: flows.length })
        : t('session.alsoUsedInOtherFlowsPlural', { count: flows.length });
    } else {
      return flows.length === 1
        ? t('session.usedInFlows', { count: flows.length })
        : t('session.usedInFlowsPlural', { count: flows.length });
    }
  };

  return (
    <Widget title={getWidgetTitle()}>
      <Stack spacing="8px">
        {flows.map((flow) => {
          // Find the slot ID for this session in the flow
          const sessionSlot = flow.slots?.find((slot) => {
            if (slot.contentType !== 'Session') return false;
            // Extract session ID from slot content (can be string or object)
            const slotSessionId =
              typeof slot.content === 'string'
                ? slot.content
                : slot.content?.origin
                  ? getObjectId(slot.content.origin)
                  : slot.content?.id;
            // Compare with session ID (check both id and origin)
            const sessionId = session.origin ? getObjectId(session.origin) : session.id;
            return slotSessionId === sessionId || slotSessionId === session.id;
          });
          return (
            <FlowWidget
              key={flow.id}
              flow={flow}
              timingInfo={flowTimingMap[flow.id]}
              sessionSlotId={sessionSlot?.id}
              event={event}
            />
          );
        })}
      </Stack>
    </Widget>
  );
};

const FlowWidget = ({
  flow,
  timingInfo,
  sessionSlotId,
  event,
}: {
  flow: FlowDto;
  timingInfo?: string;
  sessionSlotId?: string;
  event?: EventDto;
}) => {
  const navigate = useNavigate();
  const t = useTranslations();

  // Build navigation URL - shared logic to avoid duplication
  const getFlowNavigationUrl = () => {
    const eventId = event?.origin ? (typeof event.origin === 'string' ? event.origin : event.origin.id) : event?.id;
    const contextParams = eventId ? `&contextType=event&contextId=${eventId}` : '';
    if (sessionSlotId) {
      return `/flow/${flow.id}?view=slot&slotId=${sessionSlotId}${contextParams}`;
    } else {
      return `/flow/${flow.id}${contextParams ? '?' + contextParams.substring(1) : ''}`;
    }
  };

  const handleNavigateToFlow = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    navigate(getFlowNavigationUrl());
  };

  return (
    <Stack>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography sx={{ cursor: 'pointer', flex: 1 }} onClick={() => handleNavigateToFlow()} variant="body2">
          {flow.title}
        </Typography>
        <Tooltip title={t('session.navigateToFlow')}>
          <IconButton
            size="small"
            onClick={handleNavigateToFlow}
            aria-label="Go to flow"
            sx={{ color: 'text.secondary' }}
          >
            <Launch fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="subtitle3" color="text.secondary">
        {timingInfo}
      </Typography>
    </Stack>
  );
};
